import { useState } from 'react';
import { Typography, Box, IconButton, Button } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const DESC_PREVIEW = 200;

function truncateText(str, maxLen) {
  if (!str || typeof str !== 'string') return '';
  const s = str.trim();
  return s.length <= maxLen ? s : s.slice(0, maxLen).trim() + '\u2026';
}

export default function PlaceDetails({ place, onBack }) {
  const [expanded, setExpanded] = useState(false);
  if (!place) return null;

  const desc = (place.description || '').trim() || 'Sin descripción.';
  const isLong = desc.length > DESC_PREVIEW;
  const text = expanded || !isLong ? desc : truncateText(desc, DESC_PREVIEW);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        bgcolor: 'background.paper',
      }}
    >
      {place.image && (
        <Box sx={{ position: 'relative', height: 220, flexShrink: 0 }}>
          <img
            src={place.image}
            alt={place.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          {onBack && (
            <IconButton
              onClick={onBack}
              aria-label="Cerrar"
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                bgcolor: 'rgba(255,255,255,0.95)',
                boxShadow: 1,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 2.5,
          py: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 1.5,
            color: 'primary.main',
            wordBreak: 'break-word',
            fontSize: '1.2rem',
          }}
        >
          {place.title}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            lineHeight: 1.65,
            mb: 1,
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
          }}
        >
          {text}
        </Typography>
        {isLong && (
          <Button
            size="small"
            onClick={() => setExpanded((e) => !e)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', mb: 2 }}
          >
            {expanded ? 'Ver menos' : 'Ver más'}
          </Button>
        )}

        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
          {place.address && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <LocationOnIcon sx={{ color: 'text.secondary', fontSize: 20, mt: 0.25 }} />
              <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {place.address}
              </Typography>
            </Box>
          )}
          {place.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhoneIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary">
                {place.phone}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
